import React, { FormEvent, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { submitPublicContactInquiry } from '../services/api/publicContact';
import '../styles/route.css';

const CONTACT_EMAIL = 'siyamcela@shiftwagon.co.za';
const SIYAMCELA_LINKEDIN_URL = 'https://www.linkedin.com/in/siyamcela-nxuseka-a51b0687/';

const AboutPage: React.FC = () => {
  const [contactSuccess, setContactSuccess] = useState<string>('');
  const [contactError, setContactError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(event.currentTarget);
    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const subject = String(formData.get('subject') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();
    const website = String(formData.get('website') ?? '').trim();

    setContactSuccess('');
    setContactError('');
    setIsSubmitting(true);

    try {
      const successMessage = await submitPublicContactInquiry({
        name,
        email,
        subject,
        message,
        sourcePage: window.location.href,
        website,
      });
      form.reset();
      setContactSuccess(successMessage);
    } catch (error) {
      setContactError(
        error instanceof Error && error.message.trim()
          ? error.message
          : 'We could not send your message right now. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-hero about-hero">
          <div className="container about-hero-grid">
            <div className="about-hero-copy">
              <span className="sw-eyebrow">About ShiftWagon</span>
              <h1 className="sw-hero-heading">
                <span className="about-heading-primary">Modernising truck logistics</span>{' '}
                <span className="about-heading-accent">for South Africa.</span>
              </h1>
              <p className="sw-hero-sub">
                ShiftWagon is administered by Mike Innovations (Pty) Ltd, a company owned by{' '}
                <a
                  className="about-inline-link"
                  href={SIYAMCELA_LINKEDIN_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Siyamcela Nxuseka
                </a>
                , with a product focus on
                dependable bookings, faster dispatch visibility, and a more structured experience
                for customers, transport operators, and business clients.
              </p>
              <p className="sw-section-copy">
                We are building a South Africa-focused logistics platform that combines practical
                transport workflows with modern software engineering standards.
              </p>
              <div className="sw-hero-actions">
                <a className="sw-btn-primary" href="#contact">
                  Contact Us
                </a>
              </div>
              <div className="sw-pill-row">
                <div className="sw-pill sw-pill-quiet">Administered by Mike Innovations (Pty) Ltd</div>
                <div className="sw-pill sw-pill-quiet">South Africa focused</div>
                <div className="sw-pill sw-pill-quiet">Product-led logistics operations</div>
              </div>
            </div>

            <aside className="about-admin-card">
              <span className="sw-eyebrow">Company Administration</span>
              <h2 className="sw-audience-title">Operationally grounded. Professionally administered.</h2>
              <p className="sw-audience-copy">
                ShiftWagon operates as a product of Mike Innovations (Pty) Ltd. This gives the
                platform a clear administrative home, direct accountability, and an official point
                of contact for partnerships, platform questions, and customer communication.
              </p>
              <ul className="sw-audience-list">
                <li>Official business administration is handled through Mike Innovations (Pty) Ltd.</li>
                <li>Platform communication routes directly to a monitored email address and phone line.</li>
                <li>The product direction centers on practical reliability, transparency, and scale.</li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="sw-section">
          <div className="container">
            <div className="sw-section-head">
              <span className="sw-eyebrow">What We Stand For</span>
              <h2 className="sw-section-title">A logistics product built around trust and execution.</h2>
              <p className="sw-section-copy">
                The goal is not only to move goods. It is to create a cleaner, more dependable
                logistics journey for customers who need responsive bookings and for operators who
                need better structure behind every job.
              </p>
            </div>

            <div className="sw-steps sw-steps-3">
              <article className="sw-step">
                <div className="sw-step-num">01</div>
                <h3 className="sw-step-title">Clear administration</h3>
                <p className="sw-step-copy">
                  ShiftWagon is presented with an explicit company identity so users know who is
                  behind the platform and how to reach us.
                </p>
              </article>

              <article className="sw-step">
                <div className="sw-step-num">02</div>
                <h3 className="sw-step-title">Operational simplicity</h3>
                <p className="sw-step-copy">
                  Every workflow is designed to reduce confusion around bookings, coordination, and
                  delivery expectations.
                </p>
              </article>

              <article className="sw-step">
                <div className="sw-step-num">03</div>
                <h3 className="sw-step-title">Production-grade thinking</h3>
                <p className="sw-step-copy">
                  We approach the product with the same rigor used for enterprise software:
                  reliability, maintainability, and scalable architecture.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="sw-section about-contact-section" id="contact">
          <div className="container about-contact-grid">
            <div className="about-contact-copy">
              <span className="sw-eyebrow">Contact Us</span>
              <h2 className="sw-section-title">Speak with the team behind ShiftWagon.</h2>
              <p className="sw-section-copy">
                For platform questions, partnerships, operational discussions, or general support,
                contact us.
              </p>
            </div>

            <div className="about-contact-form-shell">
              <form className="about-contact-form" onSubmit={handleContactSubmit}>
                <label className="about-form-field">
                  <span>Name</span>
                  <input name="name" placeholder="Your name" required type="text" />
                </label>

                <label className="about-form-field">
                  <span>Email</span>
                  <input name="email" placeholder="you@example.com" required type="email" />
                </label>

                <label className="about-form-field">
                  <span>Subject</span>
                  <input name="subject" placeholder="How can we help?" required type="text" />
                </label>

                <label className="about-form-field">
                  <span>Message</span>
                  <textarea
                    name="message"
                    placeholder="Tell us about your inquiry, delivery need, or partnership request."
                    required
                    rows={6}
                  />
                </label>

                <label className="about-form-field about-form-honeypot" aria-hidden="true">
                  <span>Website</span>
                  <input autoComplete="off" name="website" tabIndex={-1} type="text" />
                </label>

                <button className="sw-btn-primary about-form-submit" disabled={isSubmitting} type="submit">
                  {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                </button>
                {contactSuccess ? <p className="about-form-notice">{contactSuccess}</p> : null}
                {contactError ? <p className="about-form-error">{contactError}</p> : null}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
